// BL-125: the board's lanes grouped by division (Service, Drains, Installs, in the company's order),
// "Everyone else" last; a folded group shows a thin line a technician. Each person's folds are kept
// on this browser, per company and membership, like the board's density and trays.

import type { Board, BoardTech } from './board-model.ts';

export interface Division {
  id: string;
  /** its name on the board: its own label, else the unit's */
  name: string;
  /** the business unit's own name (absent from an older server) */
  unitName?: string;
}

export interface LaneGroup {
  /** the division's id; 'rest' for Everyone else */
  key: string;
  name: string;
  techs: BoardTech[];
}

/** The id of Everyone else's group (no division, or one that's no longer a division). */
export const REST = 'rest';

/**
 * The lanes in groups: each division in order with its technicians (as the board orders them), then
 * Everyone else. A division nobody is in that day is left out. Null when the company has no
 * divisions: the board draws its lanes as before, ungrouped.
 */
export function laneGroups(
  board: Pick<Board, 'technicians'> & { divisions?: Division[] },
): LaneGroup[] | null {
  const divisions = board.divisions ?? [];
  if (!divisions.length) return null;
  const known = new Set(divisions.map((d) => d.id));
  const groups: LaneGroup[] = divisions.map((d) => ({
    key: d.id,
    name: d.name,
    techs: board.technicians.filter((t) => t.divisionId === d.id),
  }));
  const rest = board.technicians.filter((t) => !t.divisionId || !known.has(t.divisionId));
  return [...groups, { key: REST, name: 'Everyone else', techs: rest }].filter((g) => g.techs.length);
}

/**
 * The line under a moved technician's name: "Here today, usually Drains" (or "that day" on another
 * day's board); "Here today" with no home. Null when they're where they usually are.
 */
export function usuallyLine(
  t: Pick<BoardTech, 'divisionId' | 'homeDivisionId' | 'divisionMove'>,
  divisions: readonly Division[],
  isToday: boolean,
): string | null {
  if (!t.divisionMove) return null;
  const when = isToday ? 'today' : 'that day';
  const home = divisions.find((d) => d.id === t.homeDivisionId)?.name;
  return home ? `Here ${when}, usually ${home}` : `Here ${when}`;
}

/** The divisions a technician can be moved to that day: every one but where they are. */
export function moveTargets(t: Pick<BoardTech, 'divisionId'>, divisions: readonly Division[]): Division[] {
  return divisions.filter((d) => d.id !== t.divisionId);
}

/** The order the arrow keys move a visit between lanes: as drawn, a folded group's lanes skipped. */
export function laneOrderOf(
  groups: readonly LaneGroup[] | null,
  technicians: readonly Pick<BoardTech, 'id'>[],
  folded: ReadonlySet<string>,
): string[] {
  if (!groups) return technicians.map((t) => t.id);
  return groups.filter((g) => !folded.has(g.key)).flatMap((g) => g.techs.map((t) => t.id));
}

/** A group's head: "Drains, 3 technicians, 7 visits". */
export function groupLabel(g: LaneGroup): string {
  const n = g.techs.length;
  const visits = g.techs.reduce((s, t) => s + t.visits.length, 0);
  return `${g.name}, ${n} ${n === 1 ? 'technician' : 'technicians'}, ${visits} ${visits === 1 ? 'visit' : 'visits'}`;
}

// ── each person's folds, on this browser ──
const foldsKey = (tenant: string, who: string | undefined) => `fl-board-folds:${tenant}:${who ?? ''}`;

/** The groups a person folded, as they last left them; none when storage is blocked or empty. */
export function groupFolds(
  tenant: string,
  who: string | undefined,
  store?: Pick<Storage, 'getItem'>,
): Set<string> {
  try {
    const s = store ?? localStorage;
    const v = JSON.parse(s.getItem(foldsKey(tenant, who)) ?? 'null') as unknown;
    return new Set(Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}

export function keepGroupFolds(
  tenant: string,
  who: string | undefined,
  folded: ReadonlySet<string>,
  store?: Pick<Storage, 'setItem'>,
): void {
  try {
    (store ?? localStorage).setItem(foldsKey(tenant, who), JSON.stringify([...folded]));
  } catch {
    // storage blocked: the groups fold until the page is left
  }
}

/** Folds a group, or opens it. */
export function toggled(folded: ReadonlySet<string>, key: string): Set<string> {
  const next = new Set(folded);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}

// ── Setup's sheet: which units are divisions, in order ──
export interface DivisionPick {
  id: string;
  name: string;
  on: boolean;
}

/** The sheet's list: the divisions first in their order, then the other units by name. */
export function divisionPicks(
  units: readonly { id: string; name: string; active: boolean }[],
  divisions: readonly Division[],
): DivisionPick[] {
  const on = divisions.filter((d) => units.some((u) => u.id === d.id));
  const off = units
    .filter((u) => u.active && !on.some((d) => d.id === u.id))
    .sort((a, b) => a.name.localeCompare(b.name));
  return [...on.map((d) => ({ id: d.id, name: d.name, on: true })), ...off.map((u) => ({ ...u, on: false }))];
}

/** Moves a pick up (-1) or down (+1) the list. */
export function movePick(picks: readonly DivisionPick[], id: string, by: -1 | 1): DivisionPick[] {
  const i = picks.findIndex((p) => p.id === id);
  const j = i + by;
  if (i < 0 || j < 0 || j >= picks.length) return [...picks];
  const next = [...picks];
  [next[i], next[j]] = [next[j]!, next[i]!];
  return next;
}

/** businessUnits.setDivisions' list: the ticked ones, in the sheet's order. */
export const divisionIdsOf = (picks: readonly DivisionPick[]): string[] =>
  picks.filter((p) => p.on).map((p) => p.id);

/** "Moved Ana Ruiz to Drains for today." / "… for Tuesday, October 21." */
export function movedToLine(name: string, division: string, isToday: boolean, dayText: string): string {
  return `Moved ${name} to ${division} for ${isToday ? 'today' : dayText}.`;
}

/** A past move in the dialog's list: "Drains, Oct 20: Short in drains (Dana Lee). Undone." */
export function moveLine(
  m: {
    divisionId: string;
    date: string;
    reason: string | null;
    by: string | null;
    state: 'live' | 'undone' | 'replaced';
  },
  divisions: readonly Division[],
  dayText: (date: string) => string,
): string {
  const where = divisions.find((d) => d.id === m.divisionId)?.name ?? 'Another division';
  const who = m.by ? ` (${m.by})` : '';
  const state = m.state === 'undone' ? ' Undone.' : m.state === 'replaced' ? ' Changed later that day.' : '';
  // a reason is optional since 0201 (the board's Add why)
  const why = m.reason?.trim().replace(/[.!?]$/, '');
  return `${where}, ${dayText(m.date)}${why ? `: ${why}` : ''}${who}.${state}`;
}

// ── concept A (Amir, 26 Oct): the board's own grouping, arranged on the board ──

/** Everyone else's name on the board. */
export const REST_NAME = 'Everyone else';

/** A division's name, or Everyone else's. */
export const divisionName = (divisions: readonly Division[], id: string | null | undefined): string =>
  divisions.find((d) => d.id === id)?.name ?? REST_NAME;

/** The day line of a technician moved for the day: "Usually in Drains". Null when they're at home. */
export function usuallyIn(
  t: Pick<BoardTech, 'homeDivisionId' | 'divisionMove'>,
  divisions: readonly Division[],
): string | null {
  return t.divisionMove ? `Usually in ${divisionName(divisions, t.homeDivisionId)}` : null;
}

/** A division being arranged: its unit, and its name on the board (null: the unit's own). */
export interface DraftDivision {
  id: string;
  unitName: string;
  label: string | null;
}
/** The board while it's arranged: the divisions in order, and each technician's home. */
export interface Draft {
  divisions: DraftDivision[];
  /** staff id to their home division; null or absent is Everyone else */
  homes: Record<string, string | null>;
}

/** A division's name while arranged. */
export const draftName = (d: DraftDivision): string => d.label ?? d.unitName;

/** The most letters a division's name on the board has (businessUnits.arrangeDivisions' label). */
export const DIVISION_NAME_MAX = 40;

/**
 * As much of a name as fits in `max` letters, ending at a whole word (QA-767): never "…Drainage &
 * Underg". The rule or sign a cut leaves at its end ("&", "-", ",") goes too. A first word longer than
 * `max` alone is cut where it must be.
 */
export function wholeWords(name: string, max: number): string {
  const t = name.trim().replace(/\s+/g, ' ');
  if (t.length <= max) return t;
  const cut = t.slice(0, max + 1);
  const at = cut.lastIndexOf(' ');
  const kept = (at > 0 ? cut.slice(0, at) : t.slice(0, max)).replace(/[\s&+/,;:.\-–—]+$/u, '');
  return kept || t.slice(0, max);
}

/**
 * A name typed for a heading as the division keeps it: null when it's the unit's own, or empty. One too
 * long keeps its whole words (QA-767); the field said the limit as it was typed.
 */
export function labelOf(typed: string, unitName: string): string | null {
  const t = wholeWords(typed, DIVISION_NAME_MAX);
  return !t || t === unitName ? null : t;
}

/** The board's divisions now, as a draft to arrange. */
export function draftOf(
  divisions: readonly Division[],
  technicians: readonly Pick<BoardTech, 'id' | 'homeDivisionId'>[],
): Draft {
  return {
    divisions: divisions.map((d) => {
      const unitName = d.unitName ?? d.name;
      return { id: d.id, unitName, label: d.name === unitName ? null : d.name };
    }),
    homes: Object.fromEntries(technicians.map((t) => [t.id, t.homeDivisionId ?? null])),
  };
}

/**
 * The first arrangement, from staff.suggestDivisions: each unit someone mostly works in becomes a
 * division (most technicians first, then by name; at most 12), and each technician's home is their
 * unit. A unit that's retired or past the 12 leaves its technicians in Everyone else.
 */
export function suggestedDraft(
  suggestions: readonly { staffId: string; businessUnitId: string | null }[],
  units: readonly { id: string; name: string; active: boolean }[],
  max = 12,
): Draft {
  const count = new Map<string, number>();
  for (const s of suggestions)
    if (s.businessUnitId) count.set(s.businessUnitId, (count.get(s.businessUnitId) ?? 0) + 1);
  const picked = units
    .filter((u) => u.active && count.has(u.id))
    .sort((a, b) => count.get(b.id)! - count.get(a.id)! || a.name.localeCompare(b.name))
    .slice(0, max);
  const ids = new Set(picked.map((u) => u.id));
  return {
    divisions: picked.map((u) => ({ id: u.id, unitName: u.name, label: null })),
    homes: Object.fromEntries(
      suggestions.map((s) => [
        s.staffId,
        s.businessUnitId && ids.has(s.businessUnitId) ? s.businessUnitId : null,
      ]),
    ),
  };
}

/** The lanes while arranged: by home, every division drawn even with nobody in it, Everyone else last. */
export function draftGroups(draft: Draft, technicians: readonly BoardTech[]): LaneGroup[] {
  const ids = new Set(draft.divisions.map((d) => d.id));
  const homeOf = (t: BoardTech) => {
    const h = draft.homes[t.id] ?? null;
    return h && ids.has(h) ? h : null;
  };
  return [
    ...draft.divisions.map((d) => ({
      key: d.id,
      name: draftName(d),
      techs: technicians.filter((t) => homeOf(t) === d.id),
    })),
    { key: REST, name: REST_NAME, techs: technicians.filter((t) => homeOf(t) === null) },
  ];
}

/** Moves a division up (-1) or down (+1) the order. */
export function moveDivision(draft: Draft, id: string, by: -1 | 1): Draft {
  const i = draft.divisions.findIndex((d) => d.id === id);
  const j = i + by;
  if (i < 0 || j < 0 || j >= draft.divisions.length) return draft;
  const divisions = [...draft.divisions];
  [divisions[i], divisions[j]] = [divisions[j]!, divisions[i]!];
  return { ...draft, divisions };
}

/** A division's box on the board while it's arranged (its heading and its lanes), top to bottom. */
export interface DivisionSpan {
  top: number;
  bottom: number;
}

/**
 * Where a division dragged by its heading's ⋮⋮ would go (Amir, 26 Oct): a slot from 0 (first) to n
 * (after the last, before Everyone else), counting the divisions whose middle the pointer is past. The
 * spans are every division's, in the draft's order, the dragged one too.
 */
export function divisionSlot(spans: readonly DivisionSpan[], y: number): number {
  let slot = 0;
  for (const s of spans) {
    if (y <= (s.top + s.bottom) / 2) break;
    slot++;
  }
  return slot;
}

/**
 * The draft with a division put in a slot (as divisionSlot counts them): its technicians go with it,
 * as they're its by their homes. The same draft when the slot is where it already is (its own, or the
 * one after it).
 */
export function placeDivision(draft: Draft, id: string, slot: number): Draft {
  const i = draft.divisions.findIndex((d) => d.id === id);
  if (i < 0) return draft;
  const to = Math.max(0, Math.min(slot > i ? slot - 1 : slot, draft.divisions.length - 1));
  if (to === i) return draft;
  const divisions = [...draft.divisions];
  const [d] = divisions.splice(i, 1);
  divisions.splice(to, 0, d!);
  return { ...draft, divisions };
}

/** The words by the pointer for a slot: "First", "After Service"; null when it wouldn't move. */
export function slotWords(draft: Draft, id: string, slot: number): string | null {
  const next = placeDivision(draft, id, slot);
  if (next === draft) return null;
  const at = next.divisions.findIndex((d) => d.id === id);
  return at === 0 ? 'First' : `After ${draftName(next.divisions[at - 1]!)}`;
}

/** Takes a division out: its technicians go to Everyone else. */
export function removeDivision(draft: Draft, id: string): Draft {
  return {
    divisions: draft.divisions.filter((d) => d.id !== id),
    homes: Object.fromEntries(Object.entries(draft.homes).map(([k, v]) => [k, v === id ? null : v])),
  };
}

/** businessUnits.arrangeDivisions' input: the divisions in order, and the homes that changed. */
export function arrangeInput(
  draft: Draft,
  technicians: readonly Pick<BoardTech, 'id' | 'homeDivisionId'>[],
): {
  divisions: { id: string; label: string | null }[];
  homes: { staffId: string; divisionId: string | null }[];
} {
  const ids = new Set(draft.divisions.map((d) => d.id));
  return {
    divisions: draft.divisions.map((d) => ({ id: d.id, label: d.label })),
    homes: technicians.flatMap((t) => {
      const h = draft.homes[t.id] ?? null;
      const next = h && ids.has(h) ? h : null;
      const was = t.homeDivisionId ?? null;
      // a home in a division that's going goes to Everyone else, so it's sent too
      return next === was && (was === null || ids.has(was)) ? [] : [{ staffId: t.id, divisionId: next }];
    }),
  };
}

/** The toast after Done: "Everyone’s board groups by Service, Drains and Install." */
export function arrangedLine(draft: Draft): string {
  const names = draft.divisions.map(draftName);
  if (!names.length) return 'No divisions, so the board isn’t grouped.';
  const list = names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`;
  return `Everyone’s board groups by ${list}.`;
}

/**
 * The technician menu's "Today in": the other divisions they could be in for the day, and, moved, the
 * way back to their home ("Back to Service", their home). Empty without divisions.
 */
export function todayInItems(
  t: Pick<BoardTech, 'divisionId' | 'homeDivisionId' | 'divisionMove'>,
  divisions: readonly Division[],
): { label: string; items: { id: string; label: string; hint?: string }[] } | null {
  if (!divisions.length) return null;
  const now = t.divisionMove ? t.divisionId : null;
  const home = t.homeDivisionId ?? null;
  const items: { id: string; label: string; hint?: string }[] = [];
  if (t.divisionMove)
    items.push({ id: 'today:back', label: `Back to ${divisionName(divisions, home)}`, hint: 'their home' });
  for (const d of divisions)
    if (d.id !== (t.divisionId ?? null) && d.id !== (t.divisionMove ? home : null))
      items.push({ id: `today:${d.id}`, label: d.name });
  return {
    label: now ? `Today in · now in ${divisionName(divisions, now)}` : 'Today in',
    items,
  };
}
